import { extend, override } from 'flarum/common/extend';
import app from 'flarum/forum/app';
import LogInButton from 'flarum/forum/components/LogInButton';
import LogInButtons from 'flarum/forum/components/LogInButtons';
/** The forum route that starts (and finishes) the WeChat OAuth2 flow. */
const AUTH_PATH = '/auth/wechat';

/** Identifier used for the postMessage handshake with the login popup. */
const MESSAGE_TYPE = 'ziiven-wechat-oauth';

/**
 * Flarum 2.0's sign-up modal.
 *
 * It lives in a lazily loaded chunk, so it cannot be imported statically —
 * `flarum.reg.get()` returns `undefined` for a chunked module and
 * `app.modal.show(undefined, …)` throws. The registry knows how to load the
 * chunk on demand, which is what `asyncModuleImport()` does.
 */
const SIGN_UP_MODAL_PATH = 'flarum/forum/components/SignUpModal';

/**
 * Flarum 2.0 runs the initializers *before* it assigns `app.forum`
 * (`Application#boot` calls `initialize()` first, `this.forum` is set
 * afterwards). So every `app.forum.attribute()` read has to happen at render
 * time — inside an `extend` callback — and anything that needs the forum
 * object up front has to wait for the next tick.
 */
app.initializers.add('ziiven/wechat-oauth', () => {
  // ------------------------------------------------------------------
  // The login button.
  //
  // The attributes are read inside the callback, which runs when the login
  // modal is rendered — long after the app has booted.
  // ------------------------------------------------------------------
  extend(LogInButtons.prototype, 'items', function (items) {
    if (!app.forum || !app.forum.attribute('wechatOauth.enabled')) return;
    const buttonTitle = app.forum.attribute('wechatOauth.buttonTitle') || app.translator.trans('ziiven-wechat-oauth.forum.log_in_with_wechat');
    const buttonIcon = app.forum.attribute('wechatOauth.buttonIcon') || 'fab fa-weixin';
    const replaceLoginSignup = app.forum.attribute('wechatOauth.replaceLoginSignup');
    items.add('ziiven-wechat-oauth', LogInButton.component({
      className: 'Button LogInButton--wechat',
      icon: buttonIcon,
      path: AUTH_PATH
    }, buttonTitle),
    // When replacing the regular buttons, put ours first.
    replaceLoginSignup ? 100 : 0);
  });

  // Flarum's login button always opens a 580x400 popup, which is too short
  // for the WeChat QR code page.
  override(LogInButton.prototype, 'initAttrs', function (original, attrs) {
    original(attrs);
    if (attrs.path !== AUTH_PATH) return;
    attrs.onclick = function () {
      openLoginWindow(attrs.path);
    };
  });

  // ------------------------------------------------------------------
  // "WeChat login only": drop the username/password fields and the
  // "sign up" / "forgot password" links from the login modal.
  // ------------------------------------------------------------------
  extend('flarum/forum/components/LogInModal', 'fields', function (fields) {
    if (!app.forum || !app.forum.attribute('wechatOauth.replaceLoginSignup')) return;
    fields.clear();
  });

  // `override` replaces the method: the callback *is* the new method body
  // and receives the original as its first argument.
  override('flarum/forum/components/LogInModal', 'footer', function (original, ...args) {
    if (!app.forum || !app.forum.attribute('wechatOauth.replaceLoginSignup')) {
      return original.apply(this, args);
    }
    return null;
  });

  // ------------------------------------------------------------------
  // Everything below needs `app.forum`, so it waits for the boot to finish.
  // ------------------------------------------------------------------
  setTimeout(() => {
    injectButtonStyles();
    handleReturnParams();
    window.addEventListener('message', handlePopupMessage);
  }, 0);
});

/**
 * Open the WeChat authorization page.
 *
 * On desktop a popup is used so the forum stays open behind it. Mobile
 * browsers block popups more often than not, so there we navigate in the
 * current tab instead.
 */
function openLoginWindow(path) {
  if (!app.forum) return;
  const url = app.forum.attribute('baseUrl') + path;
  const width = app.forum.attribute('wechatOauth.popupWidth') || 400;
  const height = app.forum.attribute('wechatOauth.popupHeight') || 500;
  if (window.innerWidth < 768) {
    window.location.href = url;
    return;
  }
  const top = window.screenY + (window.outerHeight - height) / 2;
  const left = window.screenX + (window.outerWidth - width) / 2;
  window.open(url, 'wechatLoginPopup', `width=${width},` + `height=${height},` + `top=${top},` + `left=${left},` + 'status=no,scrollbars=yes,resizable=no');
}

/**
 * Apply the colours configured in the admin panel.
 */
function injectButtonStyles() {
  if (!app.forum) return;
  const background = app.forum.attribute('wechatOauth.buttonColor');
  const color = app.forum.attribute('wechatOauth.buttonTextColor');
  if (!background && !color) return;
  const style = document.createElement('style');
  style.textContent = `
    .LogInButton--wechat {
      ${background ? `background-color: ${background} !important;` : ''}
      ${color ? `color: ${color} !important;` : ''}
    }
  `;
  document.head.appendChild(style);
}

/**
 * Read the OAuth return parameters from the current URL.
 *
 * Used when the flow finished in the same tab (mobile), in which case the
 * callback redirected straight back to the forum.
 */
function handleReturnParams() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('_flarum_auth');
  const linked = params.get('_flarum_linked');
  if (!token && !linked) return;

  // Strip the parameters so a refresh does not re-trigger the flow.
  params.delete('_flarum_auth');
  params.delete('_flarum_linked');
  const query = params.toString();
  window.history.replaceState({}, '', window.location.pathname + (query ? `?${query}` : '') + window.location.hash);
  if (linked) {
    showLinkedNotice();
  }
  if (token) {
    openRegistration(token);
  }
}

/**
 * Handle the result the login popup posts back to this window.
 */
function handlePopupMessage(event) {
  if (event.origin !== window.location.origin) return;
  const data = event.data;
  if (!data || data.type !== MESSAGE_TYPE) return;
  if (data.linked) {
    showLinkedNotice();
  }
  if (data.token) {
    openRegistration(data.token);
    return;
  }

  // Logged in successfully — pick up the new session.
  window.location.reload();
}
function showLinkedNotice() {
  app.alerts.show({
    type: 'success'
  }, app.translator.trans('ziiven-wechat-oauth.forum.account_linked'));
}

/**
 * Open the sign-up modal pre-filled with the data WeChat provided.
 */
async function openRegistration(token) {
  if (!app.forum) return;
  try {
    const data = await app.request({
      url: app.forum.attribute('apiUrl') + '/registration-token',
      method: 'POST',
      body: {
        token
      }
    });

    // 先把 chunk 加载好，再弹窗。
    //
    // 不能直接把加载函数丢给 app.modal.show()：它内部 await 加载函数，
    // 加载失败时那个 await 会抛，`loadingModal` 就再也回不到 false ——
    // 页面会永久卡在 loading 弹窗上，而且没有任何错误提示。
    // 这里先自己 await 一次（成功后再 show 会命中缓存，秒回），
    // 失败就落到下面的 catch，只弹错误提示，不调 app.modal.show()。
    await flarum.reg.asyncModuleImport(SIGN_UP_MODAL_PATH);
    await app.modal.show(() => flarum.reg.asyncModuleImport(SIGN_UP_MODAL_PATH), {
      token,
      username: data.username || undefined,
      email: data.email || undefined,
      provided: data.provided || []
    });
  } catch (e) {
    app.alerts.show({
      type: 'error'
    }, app.translator.trans('ziiven-wechat-oauth.forum.registration_failed'));
  }
}